import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const ToastContext = createContext();

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    const meta = timers.current[id];
    if (meta?.timeout) clearTimeout(meta.timeout);
    delete timers.current[id];
  }, []);

  const showToast = useCallback(
    (toast) => {
      const id = Math.random().toString(36).slice(2);
      const item = { id, duration: 3000, ...toast };
      setToasts((t) => [...t, item]);

      // auto dismiss
      const startAuto = (duration) => {
        timers.current[id] = { remaining: duration, start: Date.now() };
        timers.current[id].timeout = setTimeout(() => dismiss(id), duration);
      };

      if (item.duration && item.duration > 0) startAuto(item.duration);

      return id;
    },
    [dismiss],
  );

  const pause = (id) => {
    const meta = timers.current[id];
    if (!meta) return;
    if (meta.timeout) {
      clearTimeout(meta.timeout);
      meta.timeout = undefined;
      if (meta.start && typeof meta.remaining === "number") {
        meta.remaining -= Date.now() - (meta.start || 0);
      }
    }
  };

  const resume = (id) => {
    const meta = timers.current[id];
    if (!meta || !meta.remaining) return;
    meta.start = Date.now();
    meta.timeout = setTimeout(() => dismiss(id), meta.remaining);
  };

  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach((m) => {
        if (m?.timeout) clearTimeout(m.timeout);
      });
      timers.current = {};
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}

      {/* Toast container */}
      <div
        aria-live="polite"
        role="status"
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 items-end"
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onMouseEnter={() => pause(t.id)}
            onMouseLeave={() => resume(t.id)}
            onClose={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

function ToastItem({ toast, onMouseEnter, onMouseLeave, onClose }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`transform transition-all duration-200 ease-out ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
    >
      <div
        role="alert"
        className="min-w-[320px]  max-w-sm rounded-xl shadow-md flex items-start gap-3 px-4 py-3 bg-black text-white "
      >
        <div className="flex-1">
          {toast.title && (
            <div className="font-medium text-sm">{toast.title}</div>
          )}
          {toast.description && (
            <div className="text-xs mt-0.5 opacity-90">{toast.description}</div>
          )}
        </div>

        <button
          aria-label="Close toast"
          onClick={onClose}
          className="ml-3 p-1 rounded hover:bg-white/10 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
